import { Stack } from "@mui/material";
import { t } from "i18next";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AppContainer from "../../../components/AppContainer";
import AppLoader from "../../../components/AppLoader";
import GlobalButton from "../../../components/CustomButton";
import useDebounce from "../../../components/hooks/useDebounce";
import DynamicPaginatedTable from "../../../components/Tables/DynamicPaginatedTable";
import { setSearchKeys } from "../../../lib/slices/search_key_slice";
import {
  getAllCompanyCustomers,
  updateCompanyCustomerStatus,
} from "../../../services/company/companyCustomersApiFn";
import { handleOpenSnackBar } from "../../../utils";
import AddEditCustomer from "./AddEditCustomer";
import GlobalTable from "../../../components/Tables/GlobalTable";
import DeleteAlert from "../../../components/Alerts/DeleteAlert";
import StatusBlockDialog from "../../../components/Dialog/StatusBlockDialog";
import { updateAllAcountStatus } from "../../../services/common/CommonApiFn";

const headerData = [
  "createdAt",
  "name",
  "email",
  "contact",
  "status",
  "actions",
];

const tableRows = [
  "created_date",
  "user_image_name",
  "email",
  "contact",
  "customerStatus",
  "actions",
];

const MyCustomersList = () => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const userData = useSelector((state) => state.userData.user);
  const searchKey = useSelector((state) => state.SearchKeysReducer.searchKeys);
  const debouncedSearchTerm = useDebounce(
    searchKey.companyClientsUsername,
    1000
  );
  const addEditCustomerRef = useRef();
  const openBlockStatusDialog = useRef();
  useEffect(() => {
    if (userData) {
      fetchAllCustomers(debouncedSearchTerm);
    }
  }, [userData, debouncedSearchTerm]);

  const fetchAllCustomers = async (name) => {
    try {
      setIsLoading(true);
      const response = await getAllCompanyCustomers(userData?._id, name);
      if (response.data.status === "success") {
        const data = response.data.data.allUsers;
        setAllUsers(data);
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    dispatch(
      setSearchKeys({
        ...searchKey,
        companyClientsUsername: e.target.value,
      })
    );
  };

  const handleOpenConfirmDialog = (e, row) => {
    openBlockStatusDialog.current.openDialog({
      type: "Client",
      status: e ? "Unblock" : "Block",
      data: row,
      event: e,
    });
  };

  const handleUpdateUserStatus = async (params) => {
    try {
      let payload = {
        status: params?.event,
        reason: params.reason,
      };
      setIsLoading(true);
      const response = await updateAllAcountStatus(
        params?.data?._id,
        payload,
        "Client"
      );
      if (response.data.status === "success") {
        fetchAllCustomers("");
        handleOpenSnackBar("success", response.data.message);
        openBlockStatusDialog.current.closeDialog({});
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const renderRightPart = () => {
    return (
      <Stack
        gap={1}
        direction={"row"}
        alignItems={"center"}
        flex={1}
        justifyContent={"flex-end"}
      >
        <GlobalButton
          variant={"custom_contained"}
          btnText={"createNew"}
          handleClickBtn={() => {
            addEditCustomerRef.current.isVisible({
              type: "add",
              fetchAllCustomers,
            });
          }}
        />
      </Stack>
    );
  };

  return (
    <React.Fragment>
      <AppLoader isVisible={isLoading} />
      <AppContainer
        primaryHeading={t("myCustomersList")}
        secondaryHeading={t("viewAllCustomers")}
        // showSerach={true}
        searchValue={searchKey.companyClientsUsername}
        onChange={handleSearchChange}
        headProps={renderRightPart()}
      >
        <GlobalTable
          tableHeader={headerData}
          displayRows={tableRows}
          tableData={allUsers}
          showActions={true}
          showEdit={true}
          handleChangeStatus={handleOpenConfirmDialog}
          handleEdit={(currentItem) => {
            addEditCustomerRef.current.isVisible({
              type: "edit",
              data: currentItem?.row,
              fetchAllCustomers,
            });
          }}
        />
      </AppContainer>
      <AddEditCustomer ref={addEditCustomerRef} />
      <StatusBlockDialog
        ref={openBlockStatusDialog}
        onConfirm={handleUpdateUserStatus}
      />
    </React.Fragment>
  );
};

export default MyCustomersList;
